import { useMemo } from "react";
import { useApiError } from "../../errors/useApiError";
import { parseApiFieldErrors } from "./parseApiFieldErrors";
import type { FieldError } from "./types";

/**
 * Default error-handling slice for `useCreateEditor` consumers that want the
 * standard wizard pattern: 400 responses with `fieldErrors` are recognized
 * and rendered inline by the editor (via `fieldError(path)`); everything else
 * (5xx, network, unexpected shapes) surfaces through the global "Whooops!"
 * toast in `useApiError.trigger`.
 *
 * Spread into the config:
 *
 *   const editorErrorConfig = useApiErrorEditorConfig();
 *   useCreateEditor(eventId, { initialState: event, save, ...editorErrorConfig });
 *
 * Pages that render their own error UI should NOT use this — wire `onError`
 * and `parseFieldErrors` explicitly so toasts don't double up with the
 * page-local error rendering.
 */
export function useApiErrorEditorConfig(): {
  onError: (e: unknown) => void;
  parseFieldErrors: (e: unknown) => Map<string, FieldError> | null;
} {
  const { trigger } = useApiError();
  return useMemo(
    () => ({
      onError: (e: unknown) => {
        if (parseApiFieldErrors(e) !== null) return;
        trigger(e);
      },
      parseFieldErrors: parseApiFieldErrors,
    }),
    [trigger],
  );
}
